import React, { useState, useEffect } from 'react';
import API_URL from '../../../config';
import { Eye, EyeOff, Upload, User } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import { AdminService } from '../../../application/services/AdminService';
import { useToast } from '../../../context/ToastContext';


const UsuarioForm = ({ onClose, onSuccess, initialData, empresas = [], isSuperAdmin = false, currentUserEmpresaId = null }) => {
    const [isLoading, setIsLoading] = useState(false);
    const toast = useToast();
    const isEditing = !!initialData;
    const [showPassword, setShowPassword] = useState(false);
    const [rolesList, setRolesList] = useState([]);
    const [previewUrl, setPreviewUrl] = useState(null); // Preview de la imagen

    const [formData, setFormData] = useState({
        nombre: '',
        cedula: '',
        direccion: '',
        telefono: '',
        id_empresa: '', 
        rol: 'Administrador',
        email: '',
        usuario: '', 
        password: '',
        foto: null 
    });

    useEffect(() => {
        if (initialData) {
            // Transformar la data que viene del backend al formato del formulario
            const emailValue = typeof initialData.email === 'object' 
                ? (initialData.email?._value || initialData.email?.value || '')
                : (initialData.email || '');
            
            // Handle rol: could be in 'roles' array, 'rol' string, or legacy 'rol' field
            let rolValue = 'Administrador';
            if (initialData.roles && Array.isArray(initialData.roles) && initialData.roles.length > 0) {
                rolValue = typeof initialData.roles[0] === 'object' 
                    ? initialData.roles[0].nombre 
                    : initialData.roles[0];
            } else if (initialData.rol) {
                rolValue = initialData.rol;
            }

            // Map legacy role codes to full names if needed
            const roleMap = {
                'A': 'Administrador',
                'P': 'Presidente',
                'T': 'Tesorero',
                'S': 'Secretario',
                'L': 'Lecturador',
                'R': 'Super Admin'
            };
            if (roleMap[rolValue]) {
                rolValue = roleMap[rolValue];
            }

            setFormData({
                nombre: initialData.nombre || '',
                cedula: initialData.cedula || '',
                direccion: initialData.direccion || '',
                telefono: initialData.telefono || '',
                id_empresa: initialData.id_empresa || initialData.empresaId || '', 
                rol: rolValue,
                email: emailValue,
                usuario: initialData.usuario || '',
                password: '', 
                foto: null 
            });
            setPreviewUrl(initialData?.foto ? `${API_URL}${initialData.foto}` : null);
        } else {
             // Default values for new user
             setFormData(prev => ({
                ...prev,
                id_empresa: isSuperAdmin ? '' : (currentUserEmpresaId || '')
             }));
        }
    }, [initialData, isSuperAdmin, currentUserEmpresaId]);

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const data = await AdminService.getRoles();
                setRolesList(data);
            } catch (error) {
                // Silently fail - fallback roles are already in the select
                console.warn('[UsuarioForm] Roles API not available, using fallback list');
            }
        };
        fetchRoles();
    }, []);

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                foto: file
            }));
            // Generate preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            // Build JSON payload (not FormData) because backend expects JSON
            const payload = {
                nombre: formData.nombre,
                cedula: formData.cedula,
                direccion: formData.direccion,
                telefono: formData.telefono,
                email: formData.email,
                usuario: formData.usuario,
                roles: formData.rol, // Backend expects 'roles' field
                id_empresa: formData.id_empresa || currentUserEmpresaId
            };

            // Only include password if provided
            if (formData.password) {
                payload.password = formData.password;
            }

            // Validation
            if (!isEditing && !payload.id_empresa && isSuperAdmin) {
                toast.error('Debe seleccionar una empresa');
                setIsLoading(false);
                return;
            }

            // Set empresa for non-super admin if not set
            if (!payload.id_empresa && !isSuperAdmin) {
                payload.id_empresa = currentUserEmpresaId;
            }

            if (isEditing) {
                await AdminService.updateUsuario(initialData.id_usuario || initialData.id, payload);
                toast.success('Usuario actualizado exitosamente');
            } else {
                await AdminService.createUsuario(payload);
                toast.success('Usuario creado exitosamente');
            }
            onSuccess();
            onClose();
        } catch (error) {
            console.error('[UsuarioForm] Error:', error);
            toast.error(error.toString() || 'Error al guardar usuario');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}
            size="lg"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={isLoading}>
                        Cancelar
                    </Button>
                    <Button onClick={handleSubmit} isLoading={isLoading}>
                        Guardar
                    </Button>
                </>
            }
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                
                {/* Foto de Perfil */}
                <div className="flex flex-col items-center justify-center p-4">
                        <div className="relative w-24 h-24 mb-4">
                        {previewUrl ? (
                            <img 
                                src={previewUrl} 
                                alt="Preview" 
                                className="w-full h-full rounded-full object-cover border-2 border-gray-200"
                            />
                        ) : (
                            <div className="w-full h-full rounded-full bg-gray-100 flex items-center justify-center border-2 border-gray-200">
                                <User className="w-12 h-12 text-gray-400" />
                            </div>
                        )}
                        <label 
                            htmlFor="file-upload"
                            className="absolute bottom-0 right-0 bg-blue-600 text-white p-1.5 rounded-full cursor-pointer hover:bg-blue-700 transition-colors"
                        >
                            <Upload className="w-4 h-4" />
                            <input 
                                id="file-upload" 
                                type="file" 
                                className="hidden" 
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                        </label>
                    </div>
                    <p className="text-sm text-gray-500">Foto de perfil (opcional)</p>
                </div>

                {/* Sección Datos Personales */}
                <div>
                        <h4 className="text-sm font-semibold text-gray-900 border-b pb-1 mb-3">Datos Personales</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Input 
                            label="Nombres" 
                            value={formData.nombre} 
                            onChange={e => handleChange('nombre', e.target.value)} 
                            required 
                            className="mb-1"
                        />
                        <Input 
                            label="Cédula" 
                            value={formData.cedula} 
                            onChange={e => handleChange('cedula', e.target.value)} 
                            required 
                            className="mb-1"
                        />
                        <Input 
                            label="Dirección" 
                            value={formData.direccion} 
                            onChange={e => handleChange('direccion', e.target.value)} 
                            className="mb-1"
                        />
                        <Input 
                            label="Teléfono" 
                            value={formData.telefono} 
                            onChange={e => handleChange('telefono', e.target.value)} 
                            className="mb-1"
                        />
                    </div>
                </div>

                {/* Sección Información de Cuenta */}
                <div>
                    <h4 className="text-sm font-semibold text-gray-900 border-b pb-1 mb-3 mt-2">Información de Cuenta</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {isSuperAdmin && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Empresa / Junta</label>
                                <select 
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                                    value={formData.id_empresa}
                                    onChange={e => handleChange('id_empresa', e.target.value)}
                                    required
                                >
                                    <option value="">Seleccione una empresa...</option>
                                    {empresas.map(emp => (
                                        <option key={emp.id} value={emp.id}>{emp.nombre}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                            <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                            <select 
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                                value={formData.rol}
                                onChange={e => handleChange('rol', e.target.value)}
                                required
                            >
                                {rolesList.length > 0 ? (
                                    rolesList.map(role => (
                                        <option key={role.id} value={role.nombre}>{role.nombre}</option>
                                    ))
                                ) : (
                                    <>
                                        <option value="Administrador">Administrador</option>
                                        <option value="Presidente">Presidente</option>
                                        <option value="Tesorero">Tesorero</option>
                                        <option value="Secretario">Secretario</option>
                                        <option value="Lecturador">Lecturador</option>
                                    </>
                                )}
                            </select>
                        </div>
                        
                        <Input 
                            label="Correo Electrónico" 
                            type="email"
                            value={formData.email} 
                            onChange={e => handleChange('email', e.target.value)} 
                            required 
                            className="mb-1"
                        />
                            <Input 
                                label="Contraseña" 
                                type={showPassword ? "text" : "password"}
                                placeholder={isEditing ? 'Deja en blanco para no cambiar' : ''}
                                value={formData.password} 
                                onChange={e => handleChange('password', e.target.value)} 
                                required={!isEditing}
                                className="mb-1"
                                rightElement={
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-5 w-5" />
                                    ) : (
                                        <Eye className="h-5 w-5" />
                                    )}
                                </button>
                                }
                            />
                            
                            <Input 
                                label="Nombre de Usuario (Login)" 
                                value={formData.usuario} 
                                onChange={e => handleChange('usuario', e.target.value)} 
                                required 
                                className="mb-1"
                            />
                    </div>
                    {/* Usuario Input moved inside grid above */}
                </div>

            </form>
        </Modal>
    );
};

export default UsuarioForm;
